import type { ClientMessage, ServerMessage } from '@wundo/shared/websocket.ts';
import { API_BASE_URL } from './api.ts';

const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

/**
 * Listen to one room, and keep listening.
 *
 * Listening is all it does: what the user says goes to the API, which can
 * answer whether it was accepted, and everything the conversation produces
 * comes back here. The session cookie authenticates the connection, so there is
 * no token to pass or refresh.
 *
 * `onReconnect` says that whatever was said while the line was down was said to
 * nobody: this socket carries no backlog, so the conversation has to be read
 * again to find out what was missed. It is not called for the first connection,
 * which has nothing to catch up on.
 *
 * Returns the way to close it.
 */
export function connectRoom(
  room: string,
  onData: (data: Record<string, unknown>) => void,
  onReconnect: () => void,
): () => void {
  const url = `${API_BASE_URL.replace(/^http/, 'ws')}/ws`;

  let socket: WebSocket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let attempts = 0;
  let closed = false;
  let connectedBefore = false;

  function open() {
    socket = new WebSocket(url);

    socket.onopen = () => {
      attempts = 0;
      const subscribe: ClientMessage = { event: 'subscribe', room };
      socket?.send(JSON.stringify(subscribe));

      if (connectedBefore) {
        onReconnect();
      }
      connectedBefore = true;
    };

    socket.onmessage = (raw) => {
      let message: ServerMessage;
      try {
        message = JSON.parse(raw.data as string) as ServerMessage;
      } catch {
        return;
      }

      if (message.event !== 'message' || message.room !== room) {
        return;
      }

      onData(message.data);
    };

    socket.onclose = () => {
      if (closed) {
        return;
      }

      attempts += 1;
      const delay = Math.min(
        INITIAL_RECONNECT_DELAY_MS * 2 ** (attempts - 1),
        MAX_RECONNECT_DELAY_MS,
      );
      reconnectTimer = setTimeout(open, delay);
    };
  }

  open();

  return () => {
    closed = true;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
    }
    socket?.close();
  };
}
